/*
 * Vajihi Labs - Email Signature Builder
 * Customize view: fill in your own personal details, brand/logo, social
 * links and disclaimer for the theme committed from Templates. UI layer
 * only — validation/rendering rules live in lib/validation.ts and
 * lib/themes.ts.
 */

/* global document, navigator */

import { getModel, updateModel } from "./lib/appState";
import { decodeShareCode, encodeShareCode } from "./lib/model";
import { getLocalLogoPreview, setLocalLogoPreview, withLocalLogoOverride } from "./lib/logoPreviewState";
import { renderScaledPreview } from "./lib/scale";
import { isValidHexColor, sanitizeUrl, validateImageFile } from "./lib/validation";
import { uploadLogo } from "./lib/logoUpload";
import { BASIC_THEME, renderTheme, SOCIAL_LABELS, SOCIAL_TYPES, THEMES } from "./lib/themes";

function themeMetaFor(id: string) {
  return id === "basic" ? BASIC_THEME : THEMES.find((t) => t.id === id);
}

const PALETTE = ["#3B5BFD", "#12172B", "#0B5738", "#906A49"];

function byId<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function setText(id: string, text: string, isError = false): void {
  const el = byId<HTMLElement>(id);
  if (!el) return;
  el.textContent = text;
  el.classList.toggle("status--error", isError);
}

function renderPreview(): void {
  const model = getModel();
  const wrapper = byId<HTMLElement>("customize-preview")?.parentElement;
  const stage = byId<HTMLElement>("customize-preview");
  if (wrapper && stage) {
    renderScaledPreview(wrapper, stage, renderTheme(withLocalLogoOverride(model)));
  }
  const chip = byId<HTMLElement>("customize-theme-chip");
  const themeMeta = themeMetaFor(model.themeId);
  if (chip) chip.textContent = themeMeta?.name ?? model.themeId;

  // Basic doesn't render brand color, the dark-logo variant, tagline/footer/
  // side statements, socials, or a disclaimer — hide those controls rather
  // than let someone fill them in and wonder why nothing shows up.
  const isBasic = model.themeId === "basic";
  const note = byId<HTMLElement>("basic-mode-note");
  const websiteField = byId<HTMLElement>("website-field");
  const brandColorField = byId<HTMLElement>("brand-color-field");
  const advancedDisclosure = byId<HTMLDetailsElement>("brand-advanced-disclosure");
  const socialDisclosure = byId<HTMLDetailsElement>("social-disclaimer-disclosure");
  if (note) note.hidden = !isBasic;
  if (websiteField) websiteField.hidden = isBasic;
  if (brandColorField) brandColorField.hidden = isBasic;
  if (advancedDisclosure) advancedDisclosure.hidden = isBasic;
  if (socialDisclosure) socialDisclosure.hidden = isBasic;

  updateSocialSummary();
}

function updateSocialSummary(): void {
  const model = getModel();
  const activeCount = model.socials.filter((s) => s.enabled && sanitizeUrl(s.url)).length;
  const linkPart = activeCount === 0 ? "No links added" : activeCount === 1 ? "1 link" : `${activeCount} links`;
  const disclaimerPart = model.disclaimer.trim() ? "Disclaimer added" : "No disclaimer";
  const el = byId<HTMLElement>("social-disclaimer-summary");
  if (el) el.textContent = `${linkPart} · ${disclaimerPart}`;
}

function syncFormFromModel(): void {
  const model = getModel();
  (byId<HTMLInputElement>("person-name-input") as HTMLInputElement).value = model.person.name;
  (byId<HTMLInputElement>("person-title-input") as HTMLInputElement).value = model.person.title;
  (byId<HTMLInputElement>("company-name-input") as HTMLInputElement).value = model.company.name;
  (byId<HTMLInputElement>("person-email-input") as HTMLInputElement).value = model.person.email;
  (byId<HTMLInputElement>("person-phone-input") as HTMLInputElement).value = model.person.phone;
  (byId<HTMLInputElement>("website-input") as HTMLInputElement).value = model.company.website;
  (byId<HTMLInputElement>("brand-color-hex") as HTMLInputElement).value = model.branding.accentOverride ?? "";
  (byId<HTMLInputElement>("tagline-input") as HTMLInputElement).value = model.branding.tagline;
  (byId<HTMLInputElement>("footer-statement-input") as HTMLInputElement).value = model.branding.footerStatement;
  (byId<HTMLInputElement>("side-statement-input") as HTMLInputElement).value = model.branding.sideStatement;
  (byId<HTMLTextAreaElement>("disclaimer-input") as HTMLTextAreaElement).value = model.disclaimer;

  renderColorSwatches();
  renderSocialToggles();
  renderSocialLinkFields();
  renderLogoState();
  renderPreview();
}

function renderColorSwatches(): void {
  const wrap = byId<HTMLElement>("color-swatches");
  if (!wrap) return;
  const model = getModel();
  wrap.innerHTML = "";
  PALETTE.forEach((hex) => {
    const active = !model.branding.useThemePalette && model.branding.accentOverride?.toUpperCase() === hex.toUpperCase();
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "color-swatch";
    btn.style.backgroundColor = hex;
    btn.setAttribute("aria-pressed", String(active));
    btn.setAttribute("aria-label", `Use ${hex} as the brand color`);
    btn.addEventListener("click", () => {
      updateModel((m) => {
        m.branding.useThemePalette = false;
        m.branding.accentOverride = hex;
      });
      syncFormFromModel();
    });
    wrap.appendChild(btn);
  });
}

function renderSocialToggles(): void {
  const wrap = byId<HTMLElement>("social-toggles");
  if (!wrap) return;
  const model = getModel();
  wrap.innerHTML = "";
  SOCIAL_TYPES.forEach((type) => {
    const entry = model.socials.find((s) => s.id === type);
    const enabled = !!entry?.enabled;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "social-toggle";
    btn.setAttribute("aria-pressed", String(enabled));
    btn.setAttribute("aria-label", SOCIAL_LABELS[type]);
    btn.title = SOCIAL_LABELS[type];
    btn.textContent = SOCIAL_LABELS[type].slice(0, 2);
    btn.addEventListener("click", () => {
      updateModel((m) => {
        const s = m.socials.find((x) => x.id === type);
        if (s) s.enabled = !s.enabled;
      });
      renderSocialToggles();
      renderSocialLinkFields();
      renderPreview();
    });
    wrap.appendChild(btn);
  });
}

function renderSocialLinkFields(): void {
  const wrap = byId<HTMLElement>("social-link-fields");
  if (!wrap) return;
  const model = getModel();
  wrap.innerHTML = "";
  const active = model.socials.filter((s) => s.enabled && s.id !== "website");
  active.forEach((entry) => {
    const field = document.createElement("div");
    field.className = "form-field";
    const inputId = `social-url-${entry.id}`;
    field.innerHTML = `<label for="${inputId}">${SOCIAL_LABELS[entry.id]} URL</label><input id="${inputId}" type="text" placeholder="https://..." />`;
    const input = field.querySelector("input") as HTMLInputElement;
    input.value = entry.url;
    input.addEventListener("input", () => {
      updateModel((m) => {
        const s = m.socials.find((x) => x.id === entry.id);
        if (s) s.url = input.value;
      });
      renderPreview();
    });
    wrap.appendChild(field);
    if (entry.enabled && !sanitizeUrl(entry.url)) {
      const hint = document.createElement("p");
      hint.className = "form-hint status--error";
      hint.textContent = `Add a link for ${SOCIAL_LABELS[entry.id]}, or turn it off — it won't show until you do.`;
      wrap.appendChild(hint);
    }
  });
}

function renderLogoState(): void {
  const model = getModel();
  const { lightBlobUrl } = getLocalLogoPreview();
  const emptyState = byId<HTMLElement>("logo-empty-state");
  const thumbRow = byId<HTMLElement>("logo-thumb-row");
  const thumb = byId<HTMLImageElement>("logo-thumb");
  const stateLabel = byId<HTMLElement>("logo-state-label");
  const filename = byId<HTMLElement>("logo-filename");

  const hasAny = !!lightBlobUrl || !!model.logos.lightSurfaceUrl;
  if (emptyState) emptyState.hidden = hasAny;
  if (thumbRow) thumbRow.hidden = !hasAny;
  if (!hasAny) return;

  if (thumb) thumb.src = lightBlobUrl || model.logos.lightSurfaceUrl;
  const ready = !!model.logos.lightSurfaceUrl && sanitizeUrl(model.logos.lightSurfaceUrl) !== "";
  if (stateLabel) {
    stateLabel.textContent = ready ? "Ready to send" : "Preview only — add a hosted URL in Settings to send it";
    stateLabel.className = `logo-thumb-row__state ${ready ? "logo-thumb-row__state--ready" : ""}`;
  }
  if (filename) filename.textContent = ready ? "Hosted logo" : "Local preview";
}

async function handleLogoFile(file: File): Promise<void> {
  const result = await validateImageFile(file);
  if (!result.ok) {
    setText("logo-status", result.reason ?? "Couldn't use that file.", true);
    return;
  }
  const { darkBlobUrl } = getLocalLogoPreview();
  setLocalLogoPreview(result.objectUrl ?? null, darkBlobUrl);
  setText("logo-status", "Uploading…");
  renderLogoState();
  renderPreview();

  const uploaded = await uploadLogo(file);
  if (uploaded.ok && uploaded.url) {
    updateModel((m) => {
      m.logos.lightSurfaceUrl = uploaded.url as string;
    });
    setText("logo-status", "Logo uploaded — ready to send.");
  } else {
    setText("logo-status", uploaded.reason || "Shown in the preview on this device only. Add a hosted URL in Settings so it actually appears when this signature is sent.");
  }
  renderLogoState();
  renderPreview();
}

async function handleDarkLogoFile(file: File): Promise<void> {
  const result = await validateImageFile(file);
  if (!result.ok) {
    setText("logo-dark-status", result.reason ?? "Couldn't use that file.", true);
    return;
  }
  const { lightBlobUrl } = getLocalLogoPreview();
  setLocalLogoPreview(lightBlobUrl, result.objectUrl ?? null);
  setText("logo-dark-status", "Uploading…");
  renderPreview();

  const uploaded = await uploadLogo(file);
  if (uploaded.ok && uploaded.url) {
    updateModel((m) => {
      m.logos.darkSurfaceUrl = uploaded.url as string;
    });
    setText("logo-dark-status", "Dark-background logo uploaded — ready to send.");
  } else {
    setText("logo-dark-status", uploaded.reason || "Uploaded — local preview only, same as the main logo.");
  }
  renderPreview();
}

function initLogoUpload(): void {
  const dropzone = byId<HTMLElement>("logo-empty-state");
  const fileInput = byId<HTMLInputElement>("logo-file-input");
  if (!dropzone || !fileInput) return;

  const openPicker = () => fileInput.click();
  dropzone.addEventListener("click", openPicker);
  dropzone.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openPicker();
    }
  });
  dropzone.addEventListener("dragover", (e) => {
    e.preventDefault();
    dropzone.classList.add("logo-dropzone--drag");
  });
  dropzone.addEventListener("dragleave", () => dropzone.classList.remove("logo-dropzone--drag"));
  dropzone.addEventListener("drop", (e) => {
    e.preventDefault();
    dropzone.classList.remove("logo-dropzone--drag");
    const file = e.dataTransfer?.files?.[0];
    if (file) void handleLogoFile(file);
  });
  fileInput.addEventListener("change", () => {
    const file = fileInput.files?.[0];
    if (file) void handleLogoFile(file);
    fileInput.value = "";
  });

  byId<HTMLButtonElement>("logo-change-btn")?.addEventListener("click", openPicker);
  byId<HTMLButtonElement>("logo-remove-btn")?.addEventListener("click", () => {
    setLocalLogoPreview(null, null);
    updateModel((m) => {
      m.logos.lightSurfaceUrl = "";
      m.logos.darkSurfaceUrl = "";
    });
    setText("logo-status", "Logo removed.");
    renderLogoState();
    renderPreview();
  });

  byId<HTMLInputElement>("logo-dark-file-input")?.addEventListener("change", (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) void handleDarkLogoFile(file);
  });
}

function bindTextField(id: string, apply: (value: string) => void): void {
  const el = byId<HTMLInputElement | HTMLTextAreaElement>(id);
  el?.addEventListener("input", () => {
    apply(el.value);
    renderPreview();
  });
}

function initBrandColor(): void {
  const hexInput = byId<HTMLInputElement>("brand-color-hex");
  if (!hexInput) return;
  hexInput.addEventListener("input", () => {
    const value = hexInput.value.trim();
    if (!value) {
      setText("brand-color-status", "");
      return;
    }
    if (!isValidHexColor(value)) {
      setText("brand-color-status", "Enter a 6-digit hex color like #3B5BFD.", true);
      return;
    }
    setText("brand-color-status", "");
    updateModel((m) => {
      m.branding.useThemePalette = false;
      m.branding.accentOverride = value.toUpperCase();
    });
    renderColorSwatches();
    renderPreview();
  });

  byId<HTMLButtonElement>("reset-theme-colors-btn")?.addEventListener("click", () => {
    updateModel((m) => {
      m.branding.useThemePalette = true;
      m.branding.accentOverride = null;
    });
    setText("brand-color-status", "");
    syncFormFromModel();
  });
}

async function copyShareCode(): Promise<void> {
  const json = encodeShareCode(getModel());
  const output = byId<HTMLTextAreaElement>("share-code-output");
  if (output) output.value = json;

  try {
    await navigator.clipboard.writeText(json);
    setText("copy-status", "Copied — paste it into Teams, WhatsApp, or email to your team.");
    return;
  } catch {
    // Clipboard API can be blocked inside the Outlook task pane's sandboxed
    // iframe — fall back to a visible, pre-selected textarea.
  }
  if (output) {
    output.hidden = false;
    output.focus();
    output.select();
    try {
      const ok = document.execCommand("copy");
      setText("copy-status", ok ? "Copied — paste it into Teams, WhatsApp, or email to your team." : "Couldn't auto-copy. The code below is selected — press Cmd+C to copy it.", !ok);
      return;
    } catch {
      // fall through
    }
  }
  setText("copy-status", "Couldn't auto-copy. Select the code below and copy it manually.", true);
}

function initImport(): void {
  byId<HTMLButtonElement>("import-code-btn")?.addEventListener("click", () => {
    const raw = byId<HTMLTextAreaElement>("import-code-input")?.value ?? "";
    const { model, errors } = decodeShareCode(raw);
    if (errors.length > 0) {
      setText("import-status", errors.join(" "), true);
      return;
    }
    // Keep this device's own personal fields — only the shareable "look"
    // fields load (decodeShareCode already refuses to touch person.*).
    const existing = getModel();
    updateModel((m) => {
      Object.assign(m, model, { person: existing.person });
    });
    syncFormFromModel();
    setText("import-status", `Loaded "${model.company.name || "Untitled"}" (${themeMetaFor(model.themeId)?.name ?? model.themeId} theme).`);
  });
}

export function initCustomize(onReview: () => void): void {
  bindTextField("person-name-input", (v) => updateModel((m) => (m.person.name = v)));
  bindTextField("person-title-input", (v) => updateModel((m) => (m.person.title = v)));
  bindTextField("company-name-input", (v) => updateModel((m) => (m.company.name = v)));
  bindTextField("person-email-input", (v) => updateModel((m) => (m.person.email = v)));
  bindTextField("person-phone-input", (v) => updateModel((m) => (m.person.phone = v)));
  bindTextField("website-input", (v) =>
    updateModel((m) => {
      m.company.website = v;
      const websiteSocial = m.socials.find((s) => s.id === "website");
      if (websiteSocial) websiteSocial.url = v;
    })
  );
  bindTextField("tagline-input", (v) => updateModel((m) => (m.branding.tagline = v)));
  bindTextField("footer-statement-input", (v) => updateModel((m) => (m.branding.footerStatement = v)));
  bindTextField("side-statement-input", (v) => updateModel((m) => (m.branding.sideStatement = v)));
  bindTextField("disclaimer-input", (v) => updateModel((m) => (m.disclaimer = v)));

  initBrandColor();
  initLogoUpload();
  initImport();

  byId<HTMLButtonElement>("copy-code-btn")?.addEventListener("click", () => {
    void copyShareCode();
  });

  byId<HTMLButtonElement>("customize-review-btn")?.addEventListener("click", onReview);

  syncFormFromModel();
}

/** Called every time Customize becomes the active view. */
export function activateCustomize(getProfile: () => { displayName: string; emailAddress: string } | null): void {
  const model = getModel();
  const looksUntouched = model.person.email === "jordan@example.com";
  if (looksUntouched) {
    const profile = getProfile();
    if (profile?.displayName || profile?.emailAddress) {
      updateModel((m) => {
        if (profile.displayName) m.person.name = profile.displayName;
        if (profile.emailAddress) m.person.email = profile.emailAddress;
      });
    }
  }
  syncFormFromModel();
}

export function refreshCustomize(): void {
  syncFormFromModel();
}

export function getCustomizePreviewHtml(): string {
  return renderTheme(withLocalLogoOverride(getModel()));
}
